import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Alert,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { showNotification } from "../../../utils/notifications";
import AppService from "../../../services/AppService";
import { getAuthToken } from "../../../utils/authUtils";
import OrganizationConfigService from "../../../services/OrganizationConfigService";

/**
 * Notification Settings component
 * A component focused on saving notification channels and events settings
 */
const NotificationSettings = ({ organizationId }) => {
  const [formData, setFormData] = useState({
    notificationChannels: [],
    notificationEvents: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load existing configuration on component mount
  useEffect(() => {
    setLoading(true);
    
    // Try to get existing config
    AppService.get(`/api/organization-config/organization/${organizationId}`)
      .then(response => {
        console.log("Loaded existing config:", response);
        // Handle response variations (data might be in response or response.data)
        const config = response.data || response;
        
        setFormData({
          notificationChannels: config.notificationChannels || [],
          notificationEvents: config.notificationEvents || []
        });
        setLoading(false);
      })
      .catch(error => {
        console.log("Error loading config or none exists yet:", error);
        setLoading(false);
      });
  }, [organizationId]);

  // Handle notification channel toggle
  const handleChannelToggle = (index) => {
    const updatedChannels = [...formData.notificationChannels];
    updatedChannels[index] = {
      ...updatedChannels[index],
      enabled: !updatedChannels[index].enabled
    };
    setFormData({
      ...formData,
      notificationChannels: updatedChannels
    });
  };
  
  // Handle notification event toggle
  const handleEventToggle = (index) => {
    const updatedEvents = [...formData.notificationEvents];
    updatedEvents[index] = {
      ...updatedEvents[index],
      enabled: !updatedEvents[index].enabled
    };
    setFormData({
      ...formData,
      notificationEvents: updatedEvents
    });
  };

  // Handle save
  const handleSave = () => {
    setSaving(true);
    setError("");
    setSuccess("");
    
    // Prepare data with only notification channels and events
    const dataToSend = {
      notificationChannels: formData.notificationChannels,
      notificationEvents: formData.notificationEvents
    };
    
    console.log("Saving notification settings:", dataToSend);
    
    // Verify authentication is available
    const token = getAuthToken();
    
    if (!token) {
      console.error("No authentication token available - authentication required");
      setError("Authentication required. Please log in and try again.");
      setSaving(false);
      showNotification("Authentication required", "error");
      return;
    }
    
    console.log("Authentication token is available, proceeding with request");
    
    // Use the dedicated notification settings endpoint
    OrganizationConfigService.updateNotificationSettings(organizationId, dataToSend)
      .then(() => {
        setSuccess("Notification settings saved successfully");
        setSaving(false);
        showNotification("Notification settings saved", "success");
      })
      .catch(error => {
        console.error("Error saving settings:", error);
        const errorMessage = error?.response?.data?.message || error.message || "Unknown error";
        setError(`Failed to save settings: ${errorMessage}`);
        setSaving(false);
        showNotification("Error saving settings", "error");
      });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          Notification Settings
        </Typography>
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <div>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Notification Channels
                </Typography>
                {formData.notificationChannels && formData.notificationChannels.length > 0 ? (
                  formData.notificationChannels.map((channel, index) => (
                    <Box key={index} mb={1.5}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={channel.enabled || false}
                            onChange={() => handleChannelToggle(index)}
                            color="primary"
                            size="small" // smaller toggle
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {channel.channelName || channel.name}
                          </Typography>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                      <Box ml={4} mt={0.25}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                          {channel.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No notification channels configured
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Notification Events
                </Typography>
                {formData.notificationEvents && formData.notificationEvents.length > 0 ? (
                  formData.notificationEvents.map((event, index) => (
                    <Box key={index} mb={1.5}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={event.enabled || false}
                            onChange={() => handleEventToggle(index)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {event.eventName || event.name}
                          </Typography>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                      <Box ml={4} mt={0.25}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                          {event.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No notification events configured
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={saving || !organizationId}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </Grid>
            </Grid>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
